import { Alert, Typography } from "@mui/material";
import { useMemo } from "react";
import { Panel } from "../layout/Panel";
import { CompGrid, type Column } from "../grid/CompGrid";
import { BaselineTooltip } from "../stats/BaselineTooltip";
import { PageErrorState } from "../pages/PageErrorState";
import { PageLoadingState } from "../pages/PageLoadingState";
import type { Span, SpanStatistics, SpanStatisticsBaselineWindow } from "../../api/client";
import { formatBaselineWindow, formatDuration } from "../../utils/formatting";
import { IdLink, SpanIdLink, TraceIdLink } from "../links/IdLink";

function spanStatsKey(serviceName: string, spanName: string): string {
  return serviceName + "\u0000" + spanName;
}

function formatDeltaPct(current: number, baseline: number): string | null {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || baseline <= 0) return null;
  const pct = ((current - baseline) / baseline) * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(0)}%`;
}

export function TraceStatsPanel(props: {
  baseline: SpanStatisticsBaselineWindow;
  serviceSlug: string | null;
  traceId: string | null;
  stats: SpanStatistics[];
  spans?: Span[] | null;
  loading: boolean;
  error: string | null;
}) {
  const { baseline, serviceSlug, traceId, stats, spans, loading, error } = props;

  const spanByKey = useMemo(() => {
    const map = new Map<string, Span>();
    if (!spans?.length) return map;
    for (const span of spans) {
      const key = spanStatsKey(span.service_name, span.span_name);
      if (!map.has(key)) map.set(key, span);
    }
    return map;
  }, [spans]);

  const columns: Column<SpanStatistics>[] = [
    {
      id: "span_id",
      label: "Span ID",
      sortable: true,
      sortValue: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        return span?.span_id ?? "";
      },
      render: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        if (!span || !serviceSlug) return <Typography variant="body2" sx={{ opacity: 0.6 }}>—</Typography>;
        return <SpanIdLink serviceSlug={serviceSlug} spanId={span.span_id} truncateAt={0} />;
      },
    },
    {
      id: "span_name",
      label: "Span Name",
      sortable: true,
      sortValue: (s) => s.span_name,
      render: (s) => {
        if (!serviceSlug) return <Typography variant="body2">{s.span_name}</Typography>;
        return (
          <IdLink to={`/services/${encodeURIComponent(serviceSlug)}/spans/perf/${encodeURIComponent(s.span_name)}`} value={s.span_name} truncateAt={0} />
        );
      },
    },
    {
      id: "duration",
      label: `Duration vs baseline ${baseline}`,
      sortable: true,
      sortValue: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        if (!span) return -1;
        return new Date(span.end_time).getTime() - new Date(span.start_time).getTime();
      },
      render: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        if (!span) return <Typography variant="body2" sx={{ opacity: 0.6 }}>—</Typography>;
        const durationMs = new Date(span.end_time).getTime() - new Date(span.start_time).getTime();
        const baselineP95 = s.baseline?.p95_duration;
        let deltaNode: React.ReactNode = null;
        if (
          baselineP95 != null &&
          Number.isFinite(baselineP95) &&
          baselineP95 > 0 &&
          Number.isFinite(durationMs)
        ) {
          const pct = ((durationMs - baselineP95) / baselineP95) * 100;
          const sign = pct >= 0 ? "+" : "";
          const text = `${sign}${pct.toFixed(0)}%`;
          deltaNode = (
            <Typography
              component="span"
              variant="caption"
              sx={{
                ml: 0.5,
                color: pct > 0 ? "error.main" : pct < 0 ? "success.main" : "text.secondary",
              }}
            >
              {text}
            </Typography>
          );
        }
        const cell = (
          <Typography component="span" variant="body2">
            <Typography component="span" variant="body2" fontWeight={600}>
              {formatDuration(durationMs)}
            </Typography>
            {deltaNode}
          </Typography>
        );
        return <BaselineTooltip baseline={s.baseline} showArrow>{cell}</BaselineTooltip>;
      },
    },
    {
      id: "start_time",
      label: "Start Time",
      sortable: true,
      sortValue: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        return span ? new Date(span.start_time).getTime() : 0;
      },
      render: (s) => {
        const span = spanByKey.get(spanStatsKey(s.service_name, s.span_name));
        if (!span) return <Typography variant="body2" sx={{ opacity: 0.6 }}>—</Typography>;
        return (
          <Typography variant="body2">{new Date(span.start_time).toLocaleString()}</Typography>
        );
      },
    },
    {
      id: "trace_id",
      label: "Trace ID",
      sortable: true,
      sortValue: () => traceId ?? "",
      render: () => {
        if (!serviceSlug || !traceId) return <Typography variant="body2" sx={{ opacity: 0.6 }}>—</Typography>;
        return <TraceIdLink serviceSlug={serviceSlug} traceId={traceId} truncateAt={16} />;
      },
    },
    {
      id: "service",
      label: "Service",
      sortable: true,
      sortValue: (s) => s.service_name,
      render: (s) => <Typography variant="body2">{s.service_name}</Typography>,
    },
  ];

  return (
    <Panel
      title="Span statistics (this trace)"
      subtitle={`Grouped by service + span name. Baseline is the window before the current range.`}
    >
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Baseline starts at the beginning of the current range ({formatBaselineWindow(baseline)} before range start).
      </Alert>
      {error ? <PageErrorState message={error} /> : null}
      {loading ? <PageLoadingState message="Loading span statistics…" sx={{ mt: 1.5 }} /> : null}
      <CompGrid
        items={stats}
        loading={loading}
        error={error}
        emptyMessage="No span statistics available"
        keyExtractor={(s, idx) => `${s.service_name}-${s.span_name}-${idx}`}
        columns={columns}
      />
    </Panel>
  );
}
